import { normaliseIcaoHex, normaliseRegistration } from '@/lib/parse';

import { REGISTER_FIELDS } from './types';
import type { AircraftRegister, AircraftRegisterFile, RegisterEntry } from './types';

/**
 * Turn the downloaded register file into something to look aircraft up in.
 *
 * Defensive to the point of paranoia about the file's shape, because the file
 * is *data*, rewritten weekly by a scheduled job against a CSV the CAA can
 * restructure without telling anybody. A register that half-parsed and fed a
 * pilot a wrong ICAO address would be worse than no register at all: they'd
 * search an ADS-B globe for somebody else's aeroplane and believe the empty
 * answer. So a row that doesn't hold up is dropped, and a file that doesn't
 * hold up is an empty register, which every caller already handles — that is
 * the same state as "not downloaded yet".
 */

/** An empty register: what the app has before the first download, and after a bad file. */
export const EMPTY_REGISTER: AircraftRegister = {
  source: '',
  retrievedAt: '',
  entries: new Map(),
};

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Whether a parsed JSON blob is shaped like the file the updater writes. */
function isRegisterFile(value: unknown): value is AircraftRegisterFile {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const file = value as Partial<AircraftRegisterFile>;
  return Array.isArray(file.aircraft) && Array.isArray(file.fields);
}

export function parseRegister(value: unknown): AircraftRegister {
  if (!isRegisterFile(value)) {
    return EMPTY_REGISTER;
  }

  // The column order is checked rather than assumed: a future field added in
  // the middle of the row would otherwise read as a model where a hex belongs.
  const ordered = REGISTER_FIELDS.every((field, index) => value.fields[index] === field);
  if (!ordered) {
    return EMPTY_REGISTER;
  }

  const entries = new Map<string, RegisterEntry>();
  for (const row of value.aircraft) {
    if (!Array.isArray(row)) {
      continue;
    }
    const registration = normaliseRegistration(text(row[0]));
    if (registration === '') {
      continue;
    }
    const icaoHex = normaliseIcaoHex(text(row[1]));
    const make = text(row[2]);
    const model = text(row[3]);
    const category = text(row[4]);
    entries.set(registration, {
      registration,
      ...(icaoHex === '' ? {} : { icaoHex }),
      ...(make === '' ? {} : { make }),
      ...(model === '' ? {} : { model }),
      ...(category === '' ? {} : { category }),
    });
  }

  const sourceLastModified = text(value.sourceLastModified);
  return {
    source: text(value.source),
    retrievedAt: text(value.retrievedAt),
    ...(sourceLastModified === '' ? {} : { sourceLastModified }),
    entries,
  };
}
